// PA cycles — one row per submission to DCH for a client (initial, then each
// renewal, plus transfers and shared-hours requests). The cycle is what a
// signature request, a nurse assessment, a PPOT loop and the LON all belong
// to, and it keeps the checklist of what the submission still needs.
//
// Not to be confused with prior_auth_history: that is the version log of
// approved authorizations, which billing reads. A cycle links to its auth row
// once approved (auth_history_id) and never pretends to be one before that.

import { supabase } from './supabase';

export const PA_ROOT_FOLDER = 'PA Submissions';
// How far ahead of the current expiry a renewal should be in DCH's hands.
export const RENEWAL_LEAD_DAYS = 45;

export const CYCLE_KIND_LABEL = { initial: 'Initial', renewal: 'Renewal', transfer: 'Transfer in', shared_hours: 'Shared hours', reconsideration: 'Reconsideration' };
// The e-signature request kind that sends a submission's family forms.
export const REQUEST_KIND_FOR_CYCLE = { initial: 'intake', renewal: 'renewal', transfer: 'transfer', reconsideration: 'renewal', shared_hours: 'renewal' };

const ordinal = (n) => { const t = n % 100; const s = (t >= 11 && t <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'); return `${n}${s}`; };
const monthYear = (d = new Date()) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
const iso = (d) => d.toISOString().slice(0, 10);

// ── checklist ────────────────────────────────────────────────────────────────
const CHECKLIST_N_FROM = '2026-11-04';
const CARE_PLAN_REVIEW_DAYS = 62;   // Rule 111-8-65-.11: nursing plans reviewed every 62 days
// Each item can tick itself from the records (auto) and can also be ticked by
// hand; a manual tick records who and when. `auto(facts, cycle)` returns
// { done, ref } or null when nothing in the records answers it.
const ITEMS = {
  nurse_assessment: { label: 'Nurse assessment', auto: (f, c) => pick(f.assessments.filter((a) => a.cycle_id === c.id || (a.status === 'complete' && after(a.assessment_date || a.updated_at, c))), (a) => ({ kind: 'assessment', id: a.id, label: `Assessment ${short(a.assessment_date || a.updated_at)}` })) },
  ppot:             { label: 'PPOT (Appendix T)', auto: (f, c) => pick(f.faxes.filter((x) => x.direction === 'inbound' && x.doc_type === 'ppot' && (x.cycle_id === c.id || after(x.received_at, c))), (x) => ({ kind: 'fax', id: x.id, label: `Received ${short(x.received_at)}` })) },
  medical_records:  { label: 'Medical records', auto: (f, c) => pick(f.faxes.filter((x) => x.direction === 'inbound' && x.doc_type === 'medical_records' && (x.cycle_id === c.id || after(x.received_at, c))), (x) => ({ kind: 'fax', id: x.id, label: `Received ${short(x.received_at)}` })) },
  signed_forms:     { label: 'Signed forms', auto: (f, c) => pick(f.packets.filter((p) => p.cycle_id === c.id && p.status === 'completed' && ['intake', 'renewal', 'transfer'].includes(p.request_kind)), (p) => ({ kind: 'packet', id: p.id, label: `Completed ${short(p.completed_at)}` })) },
  supervisory:      { label: 'Supervisory visit notes', auto: (f, c) => { const v = f.visits.filter((x) => after(x.visit_date, c, 60)); return v.length ? { done: true, ref: { kind: 'visits', label: `${v.length} on file, latest ${short(v[0].visit_date)}` } } : null; } },
  sixty_day:        { label: '60-day RN summary', auto: (f, c) => pick(f.attachments.filter((a) => a.cycle_id === c.id && /60.?day|clinical summary/i.test(a.name)), (a) => ({ kind: 'file', id: a.drive_file_id, label: a.name })) },
  caregiver_notes:  { label: 'Caregiver notes', auto: (f, c) => pick(f.attachments.filter((a) => a.cycle_id === c.id && /shift|caregiver/i.test(a.name)), (a) => ({ kind: 'file', id: a.drive_file_id, label: a.name })) },
  lon_ack:          { label: 'LON acknowledgment', auto: (f, c) => pick(f.packets.filter((p) => p.cycle_id === c.id && p.status === 'completed' && p.request_kind === 'lon'), (p) => ({ kind: 'packet', id: p.id, label: `Signed ${short(p.completed_at)}` })) },
  // Reconsideration (GAPP §831): the family's written request, backed by a
  // physician. The letter ticks itself when it comes back signed through
  // e-Signatures; otherwise it is uploaded and ticked.
  family_letter:    { label: "Family's reconsideration letter", auto: (f, c) => pick(f.packets.filter((p) => p.cycle_id === c.id && p.status === 'completed' && p.request_kind === 'other'), (p) => ({ kind: 'packet', id: p.id, label: `Signed ${short(p.completed_at)}` })) },
  lmn:              { label: 'Letter of medical necessity', auto: (f, c) => pick(f.faxes.filter((x) => x.direction === 'inbound' && x.doc_type === 'lmn' && (x.cycle_id === c.id || after(x.received_at, c))), (x) => ({ kind: 'fax', id: x.id, label: `Received ${short(x.received_at)}` })) },
  rn_justification: { label: 'RN hours justification', auto: (f, c) => pick(f.assessments.filter((a) => a.cycle_id === c.id || (a.status === 'complete' && after(a.assessment_date || a.updated_at, c))), (a) => ({ kind: 'assessment', id: a.id, label: `Assessment ${short(a.assessment_date || a.updated_at)}` })) },
  // Appendix N requires these two; the Nurse Assessment is not on it. A plan
  // written in Drive is a manual tick, or an attachment on this submission.
  // `since`: a submission already sent before these were asked for was not
  // asked for them, so it does not grow two open steps.
  care_plan:        { label: 'Nursing Care Plan', since: CHECKLIST_N_FROM, auto: (f, c) => {
    const plan = f.carePlans.find((p) => p.status === 'active' && p.rn_signed_at && after(p.reviewed_at || p.rn_signed_at, c, CARE_PLAN_REVIEW_DAYS));
    if (plan) return { done: true, ref: { kind: 'care_plan', id: plan.id, label: `Version ${plan.version_number}, signed ${short(plan.rn_signed_at)}` } };
    return pick(f.attachments.filter((a) => a.cycle_id === c.id && /care plan|plan of care/i.test(a.name)), (a) => ({ kind: 'file', id: a.drive_file_id, label: a.name }));
  } },
  skills_checklist: { label: 'Caregiver Skills Checklist', since: CHECKLIST_N_FROM, auto: (f, c) => pick(f.attachments.filter((a) => a.cycle_id === c.id && /skills? checklist|teaching plan/i.test(a.name)), (a) => ({ kind: 'file', id: a.drive_file_id, label: a.name })) },
};
// Medical records is on every kind. Where it does not apply — a shared-hours
// request leaning on what was already sent — it is marked not applicable
// rather than left open, which is the rule for every item here.
const KIND_ITEMS = {
  initial:      ['nurse_assessment', 'ppot', 'care_plan', 'skills_checklist', 'medical_records', 'signed_forms', 'lon_ack'],
  renewal:      ['nurse_assessment', 'ppot', 'care_plan', 'skills_checklist', 'medical_records', 'sixty_day', 'caregiver_notes', 'supervisory', 'signed_forms', 'lon_ack'],
  transfer:     ['nurse_assessment', 'care_plan', 'skills_checklist', 'medical_records', 'signed_forms', 'lon_ack'],
  shared_hours: ['medical_records', 'signed_forms', 'lon_ack'],
  // Only what supports the request — not the whole packet again (Appendix X:
  // "only the information that supports the request for reconsideration").
  reconsideration: ['family_letter', 'lmn', 'medical_records', 'rn_justification', 'lon_ack'],
};
function pick(rows, ref) { return rows.length ? { done: true, ref: ref(rows[0]) } : null; }
function after(date, cycle, graceDays = 0) {
  if (!date) return false;
  const start = new Date(cycle.created_at); start.setDate(start.getDate() - graceDays);
  return new Date(date) >= start;
}
const short = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

// A submission counts as sent on its submitted date; one that never went (a
// withdrawn draft) on the day it was opened.
const askedFor = (item, cycle) => !item.since || cycle.status === 'preparing'
  || String(cycle.submitted_date || cycle.created_at || '').slice(0, 10) >= item.since;

export function checklistFor(cycle, facts) {
  const keys = (KIND_ITEMS[cycle.kind] || KIND_ITEMS.renewal).filter((key) => askedFor(ITEMS[key], cycle));
  const manual = cycle.checklist || {};
  return keys.map((key) => {
    const item = ITEMS[key];
    const m = manual[key];
    // Not applicable counts as settled, not as done. A step that does not apply
    // to this submission should stop asking and stop alerting, but calling it
    // "done" would claim the paperwork exists.
    if (m?.na) return { key, label: item.label, done: false, na: true, settled: true, by: m.by || 'staff', at: m.at, manual: true };
    if (m?.done) return { key, label: item.label, done: true, settled: true, by: m.by || 'staff', at: m.at, ref: m.ref || null, manual: true };
    const a = facts ? item.auto(facts, cycle) : null;
    if (a?.done) return { key, label: item.label, done: true, settled: true, by: 'auto', ref: a.ref, manual: false };
    return { key, label: item.label, done: false, settled: false, manual: false };
  });
}

/**
 * Set an item to done, not-applicable, or back to open.
 *
 * `state` is true (done), 'na' (does not apply to this submission), or false
 * (clear it). The three are stored distinctly because they mean different
 * things to a reader: done says the paperwork exists, na says it was never
 * needed, and open says nobody has looked.
 */
// A physician's order on file for an assessment, or null. With a submission,
// it is that submission's PPOT, the same one its checklist ticks on, so last
// year's order never answers for this year's. Without one, a PPOT received in
// the twelve months before the assessment. Null means the document says so
// rather than claiming an order: the assessment normally comes first.
export function physicianOrderOnFile(facts, cycle, assessmentDate) {
  if (cycle) {
    const m = cycle.checklist?.ppot;
    if (m?.na) return null;
    if (m?.done) return `marked received by ${m.by || 'staff'}`;
    const a = ITEMS.ppot.auto(facts, cycle);
    return a?.done ? a.ref.label : null;
  }
  const from = new Date(assessmentDate || Date.now()); from.setFullYear(from.getFullYear() - 1);
  const fax = (facts?.faxes || []).find((x) => x.direction === 'inbound' && x.doc_type === 'ppot' && x.received_at && new Date(x.received_at) >= from);
  return fax ? `Received ${short(fax.received_at)}` : null;
}
export const physicianOrderLine = (order) => (order
  ? `Physician order on file (${order}) — see attached PPOT / Prior Authorization form`
  : 'No physician order (PPOT) on file when this document was generated');

export async function setManualTick(cycle, key, state, profile) {
  const next = { ...(cycle.checklist || {}) };
  const who = { by: profile?.full_name || 'staff', at: new Date().toISOString() };
  if (state === 'na') next[key] = { na: true, ...who };
  else if (state) next[key] = { done: true, ...who };
  else delete next[key];
  const { data, error } = await supabase.from('pa_cycles').update({ checklist: next, updated_at: new Date().toISOString() }).eq('id', cycle.id).select().single();
  if (error) throw error;
  return data;
}

// ── data ─────────────────────────────────────────────────────────────────────
export async function listCycles(clientId) {
  const { data } = await supabase.from('pa_cycles').select('*').eq('client_id', clientId).order('sequence', { ascending: false });
  return data || [];
}

// The records the checklist ticks from, in one round trip.
export async function loadCycleFacts(clientId) {
  const [a, p, f, v, at, cp] = await Promise.all([
    supabase.from('nurse_assessments').select('id, cycle_id, status, assessment_date, updated_at, drive_document_url, skilled_hours_requested, unskilled_hours_requested').eq('client_uuid', clientId).order('updated_at', { ascending: false }),
    supabase.from('document_packets').select('id, cycle_id, status, request_kind, completed_at, created_at, final_pdf_url').eq('client_id', clientId).order('created_at', { ascending: false }),
    supabase.from('faxes').select('id, cycle_id, direction, doc_type, received_at, sent_at, status, request_title, request_items, fulfilled_items, recipient_name, drive_url').eq('client_id', clientId).order('created_at', { ascending: false }),
    supabase.from('supervisor_visits').select('id, visit_date').eq('client_id', clientId).order('visit_date', { ascending: false }),
    supabase.from('packet_attachments').select('id, packet_id, drive_file_id, name, document_packets!inner(cycle_id, client_id)').eq('document_packets.client_id', clientId),
    supabase.from('care_plans').select('id, version_number, status, rn_signed_at, reviewed_at').eq('client_id', clientId).order('version_number', { ascending: false }),
  ]);
  return {
    assessments: a.data || [], packets: p.data || [], faxes: f.data || [], visits: v.data || [], carePlans: cp.data || [],
    attachments: (at.data || []).map((x) => ({ ...x, cycle_id: x.document_packets?.cycle_id })),
  };
}

// The cycle a new request belongs to: the latest one still being worked, or
// nothing (the caller then creates one). A reconsideration can be open beside
// it; the main submission is the one returned here.
export const isOpen = (c) => ['preparing', 'submitted'].includes(c?.status);
export const openCycle = (cycles) => (cycles || []).find((c) => isOpen(c) && c.kind !== 'reconsideration') || (cycles || []).find(isOpen) || null;
// Every submission being worked: an application, a renewal, a reconsideration.
const KIND_ORDER = { initial: 0, transfer: 0, renewal: 1, shared_hours: 1, reconsideration: 2 };
const byKind = (a, b) => (KIND_ORDER[a.kind] ?? 1) - (KIND_ORDER[b.kind] ?? 1);
export const openCycles = (cycles) => (cycles || []).filter(isOpen).sort(byKind);

// ── after the decision: DCH's Letter of Notification ─────────────────────────
// An approval is not the end of a submission. The family signs the LON (agree
// or disagree with the hours) and the office returns it to Alliant, which the
// letter asks for within 30 calendar days of its date. Until then the
// submission stays current, with those two steps showing. lon_followup marks
// decisions recorded since this existed; older approvals stay in Past.
export const awaitingLetter = (c) => c?.status === 'approved' && !!c.lon_followup && !c.lon_returned_date;
export const isCurrent = (c) => isOpen(c) || awaitingLetter(c);
export const currentCycles = (cycles) => (cycles || []).filter(isCurrent).sort(byKind);
export const LON_RETURN_DAYS = 30;
export function lonReturnDue(cycle) {
  if (!cycle?.decision_letter_date) return null;
  const d = new Date(`${String(cycle.decision_letter_date).slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + LON_RETURN_DAYS);
  return iso(d);
}

// What the family is told the decision was. A reconsideration either revises
// the hours or upholds the decision; anything else is approved, approved with
// fewer hours than were asked for, or denied.
export const decisionOutcomes = (cycle) => (cycle?.kind === 'reconsideration' ? ['revised', 'upheld'] : ['approved', 'reduced', 'denied']);
export const outcomeGrants = (o) => ['approved', 'reduced', 'revised'].includes(o);
// "Fewer hours than asked" is only known when the nurse assessment recorded
// what was asked for; otherwise it is the office's call.
export function suggestOutcome(cycle, { requested, skilled, pss } = {}) {
  if (cycle?.kind === 'reconsideration') return 'revised';
  const less = (got, asked) => asked != null && asked !== '' && got !== '' && got != null && Number(got) < Number(asked);
  return requested && (less(skilled, requested.skilled) || less(pss, requested.unskilled)) ? 'reduced' : 'approved';
}

// ── what a submission does ───────────────────────────────────────────────────
// A renewal or a reconsideration never moves the client's status. A renewal is
// for a child in service; a reconsideration is usually about the hours a child
// in service was approved for, and a denied child stays denied until DCH
// answers it. Only an initial or a transfer in implies Submitted (DMA80).
export const submissionKeepsStatus = (cycle) => ['renewal', 'reconsideration', 'shared_hours'].includes(cycle?.kind);
// The words the family email uses for it.
export const familyEmailKind = (cycle) => (['renewal', 'reconsideration'].includes(cycle?.kind) ? cycle.kind : 'application');

// What may be started now. Three kinds of submission run side by side, one
// of each at a time:
//   application   — an initial or a transfer in
//   renewal       — the next authorization for a child already authorized
//   reconsideration — beside either (GAPP §818.10 keeps a renewal due during
//                     an appeal)
// A transfer can need a renewal at once: Pahel Shah (G079) transferred in on a
// PA the previous agency never disclosed, said by the family to end within the
// month, so the renewal had to start while the transfer was still with DCH.
const APPLICATION = ['initial', 'transfer'];
export function startableKinds(cycles) {
  const open = (cycles || []).filter(isOpen);
  const has = (test) => open.some((c) => test(c.kind));
  const application = !has((k) => APPLICATION.includes(k));
  const renewal = !has((k) => k === 'renewal');
  return {
    application,                  // initial, transfer in
    renewal,
    main: application || renewal, // any of the three main kinds
    reconsideration: !has((k) => k === 'reconsideration'),
  };
}
export const canStart = (startable, kind) => (kind === 'reconsideration' ? startable.reconsideration : kind === 'renewal' ? startable.renewal : startable.application);

// GAPP §819.2: 30 calendar days from the date on the Determination Letter.
export const RECONSIDERATION_DAYS = 30;
export function reconsiderationDueFrom(letterDate) {
  if (!letterDate) return null;
  const d = new Date(`${String(letterDate).slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + RECONSIDERATION_DAYS);
  return iso(d);
}
export const currentApproved = (cycles) => (cycles || []).find((c) => c.status === 'approved') || null;

export function cycleLabel(kind, sequence, when = new Date()) {
  if (kind === 'initial') return `Initial · ${monthYear(when)}`;
  if (kind === 'renewal') return `${ordinal(Math.max(1, sequence - 1))} renewal · ${monthYear(when)}`;
  return `${CYCLE_KIND_LABEL[kind] || kind} · ${monthYear(when)}`;
}

// When the next renewal needs to be in: the current PA expiry minus the lead.
export function renewalDueFrom(expiry) {
  if (!expiry) return null;
  const d = new Date(expiry); d.setDate(d.getDate() - RENEWAL_LEAD_DAYS);
  return iso(d);
}

// Create the next cycle for a client and its Drive folder. The folder is
// created now so anything filed for the cycle has somewhere to land.
export async function createCycle({ client, kind, dueDate, profile, existing, extra = {} }) {
  const cycles = existing || (await listCycles(client.id));
  let sequence = (cycles[0]?.sequence || 0) + 1;
  if (!cycles.length && kind === 'renewal') {
    // No cycles recorded yet (history predates them): count the approved PAs
    // so the label still says which renewal this is.
    const { count } = await supabase.from('prior_auth_history').select('id', { count: 'exact', head: true }).eq('client_id', client.id).eq('auth_type', 'PA');
    sequence = Math.max(sequence, (count || 0) + 1);
  }
  const label = cycleLabel(kind, sequence);
  const drive_folder_name = `${PA_ROOT_FOLDER}/${label}`;
  let drive_folder_id = null;
  try {
    const { data } = await supabase.functions.invoke('drive-file', { body: { action: 'ensure_path', clientCode: client.client_id, path: drive_folder_name } });
    if (data?.success) drive_folder_id = data.folderId;
  } catch (_) { /* the folder is created on first upload if this failed */ }
  const { data, error } = await supabase.from('pa_cycles').insert({
    client_id: client.id, kind, sequence, label, status: 'preparing',
    due_date: dueDate || null, drive_folder_id, drive_folder_name, created_by: profile?.id || null,
    ...extra,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function updateCycle(id, patch) {
  const { data, error } = await supabase.from('pa_cycles').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

// Folder path a request files into: the cycle's, or the old flat folder when
// a request has no cycle (an "Other" document that belongs to nothing).
export const cycleFolder = (cycle, packet) => cycle?.drive_folder_name || (packet && !packet.client_id ? 'e-Signatures' : 'Signing Packets');
